package one.healthium.insights.ui.personal_mealplan

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import androidx.compose.ui.util.fastForEachIndexed
import com.composables.core.ModalBottomSheetState
import com.composables.core.SheetDetent.Companion.Hidden
import loco.MEAL_PLAN_LOG_FOOD_BUTTON
import loco.RECIPE_INGREDIENTS_TITLE
import loco.RECIPE_NUTRITION_CALORIES
import loco.RECIPE_NUTRITION_CARBS
import loco.RECIPE_NUTRITION_FAT
import loco.RECIPE_NUTRITION_FIBER
import loco.RECIPE_NUTRITION_PROTEIN
import loco.RECIPE_INGREDIENT_FORMAT
import loco.RECIPE_NUTRITION_PER_100G
import loco.RECIPE_PREPARATION_TITLE
import loco.RECIPE_SERVING_SIZE_FORMAT
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.toString
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.IngredientModel
import one.healthium.insights.domain.models.NutritionModel
import one.healthium.insights.domain.models.RecipeModel
import one.healthium.insights.domain.models.toLogMealAction
import one.healthium.loco.resolve
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.components.AppModalBottomSheet
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import one.healthium.ui.compose.theme.gray_7
import org.jetbrains.compose.resources.stringResource

@Composable
fun RecipeBottomSheet(
    sheetState: ModalBottomSheetState,
    recipe: RecipeModel,
    mealType: String,
    onDismissed: () -> Unit,
    onLogMeal: (ActionModel.LogMeal) -> Unit = {},
) {
    AppModalBottomSheet(
        state = sheetState,
        onDismissed = onDismissed,
    ) {
        RecipeDetailContent(
            recipe = recipe,
            mealType = mealType,
            onDismiss = { sheetState.targetDetent = Hidden },
            onLogMeal = onLogMeal,
        )
    }
}

private val BadgeBackground = gray_7
private val SecondaryTextColor = gray_3
private val DividerColor = Color.Black.copy(alpha = 0.15f)

@Composable
private fun RecipeDetailContent(
    recipe: RecipeModel,
    mealType: String,
    onDismiss: (() -> Unit)? = null,
    onLogMeal: (ActionModel.LogMeal) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
    ) {
        if (onDismiss != null) {
            AppSheetTopBar(
                onCloseClicked = onDismiss,
            )
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 20.dp),
        ) {
            // Recipe title
            Text(
                text = recipe.title,
                style = AppTheme.textStyles.poppins26Bold,
                color = AppTheme.colors.text100,
            )

            Spacer(modifier = Modifier.height(12.dp))

            // --- Meal type badge + Log button + subtitle ---
            RecipeHeader(
                emoji = recipe.emoji,
                mealType = mealType,
                servingWeightGrams = recipe.servingWeightGrams,
                caloriesPerServing = recipe.caloriesPerServing,
                onLogMeal = {
                    onLogMeal(recipe.toLogMealAction())
                },
            )

            Spacer(modifier = Modifier.height(20.dp))
            HorizontalDivider(color = DividerColor, thickness = 1.dp)
            Spacer(modifier = Modifier.height(20.dp))

            // --- Nutrition section ---
            NutritionSection(nutrition = recipe.nutritionPer100g)

            Spacer(modifier = Modifier.height(20.dp))
            HorizontalDivider(color = DividerColor, thickness = 1.dp)
            Spacer(modifier = Modifier.height(20.dp))

            // --- Ingredients section ---
            IngredientsSection(ingredients = recipe.ingredients)

            Spacer(modifier = Modifier.height(20.dp))
            HorizontalDivider(color = DividerColor, thickness = 1.dp)
            Spacer(modifier = Modifier.height(20.dp))

            // --- Preparation section ---
            PreparationSection(
                steps = recipe.steps,
                servingWeightGrams = recipe.servingWeightGrams,
            )

            Spacer(modifier = Modifier.height(96.dp))
        }
    }
}

// --- Header ---

@Composable
private fun RecipeHeader(
    emoji: String,
    mealType: String,
    servingWeightGrams: Int,
    caloriesPerServing: Int,
    onLogMeal: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth()) {
        // Meal type badge + Log button
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = mealType,
                style = AppTheme.textStyles.secondary_14_400,
                color = AppTheme.colors.text100,
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .background(BadgeBackground)
                    .padding(horizontal = 10.dp, vertical = 4.dp),
            )
            Spacer(modifier = Modifier.weight(1f))
            AppButton(
                minHeight = 36.dp,
                contentPadding = PaddingValues(horizontal = 16.dp),
                colors = AppButtonDefaults.appButtonColors(
                    contentColor = AppTheme.colors.textInverted100,
                    containerColor = AppTheme.colors.secondary,
                ),
                onClick = onLogMeal,
            ) {
                Text(
                    text = stringResource(loco.Res.string.MEAL_PLAN_LOG_FOOD_BUTTON),
                    style = AppTheme.textStyles.secondary_14_500,
                )
            }
        }

        Spacer(modifier = Modifier.height(10.dp))

        // Serving info with calories
        Text(
            text = "$emoji ${servingWeightGrams.toFloat().formatGrams().resolve()} · ${
                caloriesPerServing.toFloat().formatCalories().resolve()
            }",
            style = AppTheme.textStyles.secondary_16_400,
            color = SecondaryTextColor,
        )
    }
}

// --- Nutrition Section ---

@Composable
private fun NutritionSection(
    nutrition: NutritionModel,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(
            text = stringResource(loco.Res.string.RECIPE_NUTRITION_PER_100G),
            style = AppTheme.textStyles.secondary_20_700,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(4.dp))

        NutritionLine(label = stringResource(loco.Res.string.RECIPE_NUTRITION_CALORIES), value = nutrition.calories.toFloat().formatCalories().resolve())
        NutritionLine(label = stringResource(loco.Res.string.RECIPE_NUTRITION_PROTEIN), value = nutrition.protein.toFloat().formatGrams().resolve())
        NutritionLine(label = stringResource(loco.Res.string.RECIPE_NUTRITION_FAT), value = nutrition.fat.toFloat().formatGrams().resolve())
        NutritionLine(label = stringResource(loco.Res.string.RECIPE_NUTRITION_CARBS), value = nutrition.carbs.toFloat().formatGrams().resolve())
        NutritionLine(label = stringResource(loco.Res.string.RECIPE_NUTRITION_FIBER), value = nutrition.fiber.toFloat().formatGrams().resolve())
    }
}

@Composable
private fun NutritionLine(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = "$label: $value",
        style = AppTheme.textStyles.secondary_16_400,
        color = AppTheme.colors.text100,
        modifier = modifier,
    )
}

// --- Ingredients Section ---

@Composable
private fun IngredientsSection(
    ingredients: List<IngredientModel>,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(
            text = stringResource(loco.Res.string.RECIPE_INGREDIENTS_TITLE),
            style = AppTheme.textStyles.secondary_20_700,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(4.dp))

        ingredients.fastForEach { ingredient ->
            Text(
                text = "${ingredient.emoji} " + stringResource(
                    loco.Res.string.RECIPE_INGREDIENT_FORMAT,
                    ingredient.name,
                    ingredient.amount.toFloat().toString(1),
                    ingredient.unit,
                ),
                style = AppTheme.textStyles.secondary_16_400,
                color = AppTheme.colors.text100,
            )
        }
    }
}

// --- Preparation Section ---

@Composable
private fun PreparationSection(
    steps: List<String>,
    servingWeightGrams: Int,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(
            text = stringResource(loco.Res.string.RECIPE_PREPARATION_TITLE),
            style = AppTheme.textStyles.secondary_20_700,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(4.dp))

        // Serving size badge
        Text(
            text = stringResource(
                loco.Res.string.RECIPE_SERVING_SIZE_FORMAT,
                servingWeightGrams.toFloat().formatGrams().resolve(),
            ),
            style = AppTheme.textStyles.secondary_14_400,
            color = AppTheme.colors.text100,
            modifier = Modifier
                .clip(RoundedCornerShape(6.dp))
                .background(BadgeBackground)
                .padding(horizontal = 10.dp, vertical = 4.dp),
        )

        Spacer(modifier = Modifier.height(4.dp))

        steps.fastForEachIndexed { index, step ->
            Text(
                text = "${index + 1}. $step",
                style = AppTheme.textStyles.secondary_16_400,
                color = AppTheme.colors.text100,
            )
        }
    }
}

// --- Previews ---

@Preview(showBackground = true)
@Composable
private fun RecipeDetailContentPreview() {
    AppTheme {
        RecipeDetailContent(
            recipe = previewRecipe,
            mealType = "Breakfast",
            onDismiss = {},
        )
    }
}

private val previewRecipe = RecipeModel(
    id = "1",
    title = "Classic Omelette",
    emoji = "\uD83C\uDF73",
    servingWeightGrams = 228,
    ingredients = listOf(
        IngredientModel(name = "eggs", emoji = "🥚", amount = 4.0, unit = "pcs"),
        IngredientModel(name = "butter", emoji = "🧈", amount = 1.0, unit = "tbsp"),
        IngredientModel(name = "salt", emoji = "🧂", amount = 0.5, unit = "tsp"),
        IngredientModel(name = "pepper", emoji = "🌶️", amount = 0.25, unit = "tsp"),
    ),
    steps = listOf(
        "Crack eggs into a bowl and beat until well combined.",
        "Heat butter in a non-stick pan over medium heat.",
        "Pour egg mixture into pan and cook for 2-3 minutes.",
        "Fold omelette in half and serve immediately.",
    ),
    nutritionPer100g = NutritionModel(
        calories = 185.0,
        protein = 11.7,
        fat = 14.6,
        carbs = 2.1,
    ),
)
